/**
 * Tiny offline store + sync queue using localStorage.
 * Stores route data + queued mark-complete / photo-upload actions.
 * On network recovery, drain the queue.
 */
const ROUTE_KEY = "rd_driver_route";
const QUEUE_KEY = "rd_driver_queue";

export function cacheRoute(route) {
    try { localStorage.setItem(ROUTE_KEY, JSON.stringify({ route, cached_at: Date.now() })); } catch {}
}

export function readCachedRoute() {
    try {
        const raw = localStorage.getItem(ROUTE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}

export function enqueueAction(action) {
    try {
        const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
        q.push({ ...action, queued_at: Date.now(), id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
        localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
        return q.length;
    } catch { return 0; }
}

export function readQueue() {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
}

export function clearActionById(id) {
    try {
        const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]").filter((a) => a.id !== id);
        localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    } catch {}
}

export async function drainQueue(api, onProgress) {
    const queue = readQueue();
    let drained = 0;
    let networkDown = false;
    for (const action of queue) {
        if (networkDown) break;
        try {
            if (action.type === "stop_complete") {
                // Replay the ORIGINAL enqueued patch — status could be
                // "completed", "cart_not_out", "missed", etc. Never
                // overwrite what the driver actually recorded.
                const {
                    // Fields we don't want to send back to the API
                    id: _id, queued_at: _qa, type: _t,
                    route_id, stop_id,
                    ...patch
                } = action;
                if (!route_id || !stop_id) {
                    // Malformed entry from an older client — drop it so
                    // it doesn't jam the drain forever.
                    clearActionById(action.id);
                    continue;
                }
                await api.patch(`/routes/${route_id}/stops/${stop_id}`, patch);
                clearActionById(action.id);
                drained += 1;
                onProgress?.(drained, queue.length);
            } else if (action.type === "stop_photo") {
                // Photo was captured offline (or the online upload failed).
                // Decode the queued data URL, upload, then patch the stop.
                const { route_id, stop_id, photo_data_url, photo_filename } = action;
                if (!route_id || !stop_id || !photo_data_url) {
                    clearActionById(action.id);
                    continue;
                }
                const blob = await (await fetch(photo_data_url)).blob();
                const fd = new FormData();
                fd.append("file", blob, photo_filename || "cart.jpg");
                const uploadResp = await api.post("/uploads?kind=stop_photo", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const photo_url = `/api/uploads/${uploadResp.data.id}`;
                await api.patch(`/routes/${route_id}/stops/${stop_id}`, { photo_url });
                clearActionById(action.id);
                drained += 1;
                onProgress?.(drained, queue.length);
            } else {
                // Unknown action type — discard rather than loop forever.
                clearActionById(action.id);
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                // Auth failed — stop draining; the api.js interceptor
                // will handle redirecting the user to log in again.
                networkDown = true;
                break;
            }
            if (status === 404 || status === 410 || status === 422) {
                // The stop no longer exists (customer moved off route,
                // route deleted, etc). Dropping this action prevents it
                // from blocking every other queued item behind it.
                clearActionById(action.id);
                continue;
            }
            if (status && status >= 400 && status < 500) {
                // Any other permanent client error — drop it so the queue
                // can keep draining. The driver's action isn't lost from
                // their point of view (route already reflects it locally).
                clearActionById(action.id);
                continue;
            }
            // 5xx / network — genuine transient issue. Stop draining and
            // try again next tick so ordering is preserved for the same
            // stop across retries.
            networkDown = true;
            break;
        }
    }
    return drained;
}
